import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Rechnung } from '../types';
import rechnungService from '../services/rechnungService';
import {
  Card, Badge, LoadingScreen, Icon, Button, Field, Input, DateInput, Textarea, FormActions, Modal,
} from '../components/ui';
import {
  RECHNUNG_STATUS_LABEL, RECHNUNG_STATUS_COLOR, UEBERFAELLIG_COLOR, fmtDatum,
} from '../config/rechnungStatus';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { formatBetrag, formatPreis, formatMwst, textZuZahl } from '../utils/preis';
import { ZurueckLink } from '../components/ZurueckLink';

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-gray-100 py-2.5 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-right text-sm font-medium text-gray-900">{value ?? '–'}</span>
  </div>
);

/** Position von Hand ergänzen – nur im Entwurf. */
const PositionModal: React.FC<{
  rechnungId: number;
  onClose: () => void;
  onSaved: (r: Rechnung) => void;
}> = ({ rechnungId, onClose, onSaved }) => {
  const [bezeichnung, setBezeichnung] = useState('');
  const [mengeText, setMengeText] = useState('1');
  const [preisText, setPreisText] = useState('');
  const [mwstText, setMwstText] = useState('19');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const menge = textZuZahl(mengeText);
    const einzelpreis = textZuZahl(preisText);
    const mwstSatz = textZuZahl(mwstText);
    if (!bezeichnung.trim()) { toast.error('Bezeichnung fehlt'); return; }
    if (menge == null || menge <= 0) { toast.error('Menge muss größer als 0 sein'); return; }
    if (einzelpreis == null) { toast.error('Preis ist keine gültige Zahl'); return; }
    if (mwstSatz == null || mwstSatz > 100) { toast.error('MwSt-Satz muss zwischen 0 und 100 liegen'); return; }

    setSaving(true);
    try {
      onSaved(await rechnungService.addPosition(rechnungId, {
        bezeichnung: bezeichnung.trim(), menge, einzelpreis, mwstSatz,
        leistungVon: von || null, leistungBis: bis || von || null,
      }));
      toast.success('Position hinzugefügt');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Position konnte nicht hinzugefügt werden');
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Position hinzufügen" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Bezeichnung" required>
          <Input required value={bezeichnung} onChange={e => setBezeichnung(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Menge" required>
            <Input required inputMode="decimal" value={mengeText} onChange={e => setMengeText(e.target.value)} />
          </Field>
          <Field label="Preis netto" required>
            <Input required inputMode="decimal" value={preisText} onChange={e => setPreisText(e.target.value)} />
          </Field>
          <Field label="MwSt %" required>
            <Input required inputMode="decimal" value={mwstText} onChange={e => setMwstText(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Leistung von" hint="Pflichtangabe auf der Rechnung">
            <DateInput value={von} onChange={setVon} />
          </Field>
          <Field label="Leistung bis" hint="Leer = wie „von“">
            <DateInput value={bis} onChange={setBis} />
          </Field>
        </div>
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button type="submit" loading={saving}>Hinzufügen</Button>
        </FormActions>
      </form>
    </Modal>
  );
};

/**
 * Rechnungs-Detailseite. Im Entwurf sind Empfänger, Notiz und Positionen änderbar; ab „Gestellt"
 * ist alles gesperrt — Korrekturen laufen ausschließlich über Storno und eine neue Rechnung.
 */
const RechnungDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const rechnungId = parseInt(id || '', 10);

  const [rechnung, setRechnung] = useState<Rechnung | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editEmpfaenger, setEditEmpfaenger] = useState(false);
  const [form, setForm] = useState<any>({});
  const [zeigePositionModal, setZeigePositionModal] = useState(false);

  const load = useCallback(async () => {
    if (!rechnungId) return;
    try { setRechnung(await rechnungService.getById(rechnungId)); }
    catch { toast.error('Rechnung konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, [rechnungId]);

  useEffect(() => { load(); }, [load]);

  // `statuswechsel`: der Status-Badge tauscht sichtbar (ui-wechsel), nach dem Stellen leuchtet die Nummer auf.
  // `erfolg` darf eine Funktion sein, damit z. B. die frisch gezogene Rechnungsnummer im Toast steht.
  const aktion = async (
    texte: { erfolg: string | ((ergebnis: Rechnung) => string); fehler: string },
    fn: () => Promise<Rechnung>, frage?: string, statuswechsel?: 'stellen' | true,
  ) => {
    if (frage && !window.confirm(frage)) return;
    setBusy(true);
    try {
      const ergebnis = await fn();
      if (statuswechsel) markiereNeu(`rechnung-${rechnungId}`);
      if (statuswechsel === 'stellen') markiereNeu(`rechnungsnummer-${rechnungId}`);
      toast.success(typeof texte.erfolg === 'function' ? texte.erfolg(ergebnis) : texte.erfolg);
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || texte.fehler);
    } finally { setBusy(false); }
  };

  const speichereEmpfaenger = async () => {
    setBusy(true);
    try {
      await rechnungService.update(rechnungId, form);
      setEditEmpfaenger(false);
      markiereNeu(`rechnungsdaten-${rechnungId}`);
      toast.success('Rechnungsdaten gespeichert');
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Rechnungsdaten konnten nicht gespeichert werden');
    } finally { setBusy(false); }
  };

  if (loading) return <LoadingScreen />;
  if (!rechnung) return <div className="p-6">Rechnung nicht gefunden. <Link to="/rechnungen" className="text-brand-600">Zurück</Link></div>;

  const r = rechnung;
  const entwurf = r.status === 'ENTWURF';
  const statusNeu = ankunft(`rechnung-${r.id}`);
  const nummerNeu = ankunft(`rechnungsnummer-${r.id}`);
  const datenNeu = ankunft(`rechnungsdaten-${r.id}`);

  return (
    <div className="mx-auto max-w-3xl p-6">
      <ZurueckLink fallback="/rechnungen" fallbackLabel="Zurück zu den Rechnungen" />

      <div className="mb-6 mt-3 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {r.rechnungsnummer ? `Rechnung ${r.rechnungsnummer}` : 'Rechnungsentwurf'}
        </h1>
        <Badge key={r.status} className={`${RECHNUNG_STATUS_COLOR[r.status]} ${statusNeu.className && 'ui-wechsel'}`}>{RECHNUNG_STATUS_LABEL[r.status]}</Badge>
        {r.ueberfaellig && <Badge className={UEBERFAELLIG_COLOR}>Überfällig</Badge>}
      </div>

      {/* Aktionen je Status */}
      <Card className="mb-6 flex flex-wrap items-center gap-2 p-4">
        {entwurf && (
          <>
            <Button
              loading={busy}
              onClick={() => aktion({
                erfolg: g => (g.rechnungsnummer ? `Rechnung ${g.rechnungsnummer} gestellt` : 'Rechnung gestellt'),
                fehler: 'Rechnung konnte nicht gestellt werden',
              }, () => rechnungService.stellen(r.id),
              'Rechnung stellen? Sie bekommt eine Rechnungsnummer und ist danach unveränderlich.', 'stellen')}
            >
              Rechnung stellen
            </Button>
            <Button
              variant="danger" disabled={busy}
              onClick={() => aktion({ erfolg: 'Rechnungsentwurf gelöscht', fehler: 'Rechnungsentwurf konnte nicht gelöscht werden' }, async () => {
                await rechnungService.delete(r.id);
                window.location.href = '/rechnungen';
                return r;
              }, 'Rechnungsentwurf löschen? Seine Posten gelten danach wieder als offen.')}
            >
              Entwurf löschen
            </Button>
          </>
        )}
        {r.status === 'GESTELLT' && (
          <>
            <Button loading={busy} onClick={() => aktion({
              erfolg: `Rechnung ${r.rechnungsnummer} als bezahlt vermerkt`,
              fehler: 'Rechnung konnte nicht als bezahlt vermerkt werden',
            }, () => rechnungService.bezahlt(r.id), undefined, true)}>
              Als bezahlt vermerken
            </Button>
            <Button
              variant="secondary" disabled={busy}
              onClick={() => aktion({
                erfolg: `Rechnung ${r.rechnungsnummer} storniert`,
                fehler: 'Rechnung konnte nicht storniert werden',
              }, () => rechnungService.stornieren(r.id),
                'Rechnung stornieren? Es entsteht eine Storno-Rechnung mit negativen Beträgen, die Posten werden wieder offen.', true)}
            >
              Stornieren
            </Button>
          </>
        )}
        {r.hatPdf && (
          <Button variant="secondary" disabled={busy}
            onClick={() => rechnungService.downloadPdf(r.id, r.rechnungsnummer)}
            icon={<Icon.Doc className="h-4 w-4" />}>
            PDF herunterladen
          </Button>
        )}
        {!entwurf && (
          <span className="ml-auto text-xs text-gray-500">
            Gestellte Rechnungen sind unveränderlich — Korrekturen laufen über Storno.
          </span>
        )}
      </Card>

      {/* Storno-Kette */}
      {(r.storniertVonRechnungsnummer || r.storniertRechnungsnummer) && (
        <Card className="mb-6 border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {r.storniertVonRechnungsnummer && (
            <>Diese Rechnung wurde aufgehoben durch{' '}
              <Link to={`/rechnungen/${r.storniertVonRechnungId}`} className="font-semibold underline">
                {r.storniertVonRechnungsnummer}
              </Link>.</>
          )}
          {r.storniertRechnungsnummer && (
            <>Storno-Rechnung zu{' '}
              <Link to={`/rechnungen/${r.storniertRechnungId}`} className="font-semibold underline">
                {r.storniertRechnungsnummer}
              </Link>.</>
          )}
        </Card>
      )}

      {/* Rechnungsdaten */}
      <Card ref={datenNeu.ref} className={`mb-6 p-6 ${datenNeu.className}`}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Rechnungsdaten</h2>
          {entwurf && !editEmpfaenger && (
            <button
              onClick={() => { setForm({
                empfaengerName: r.empfaengerName ?? '', empfaengerZusatz: r.empfaengerZusatz ?? '',
                empfaengerStrasse: r.empfaengerStrasse ?? '', empfaengerPlz: r.empfaengerPlz ?? '',
                empfaengerOrt: r.empfaengerOrt ?? '', notizen: r.notizen ?? '',
              }); setEditEmpfaenger(true); }}
              className="text-sm font-medium text-brand-700 hover:underline"
            >
              Bearbeiten
            </button>
          )}
        </div>

        {editEmpfaenger ? (
          <div className="space-y-3">
            <Field label="Name"><Input value={form.empfaengerName} onChange={e => setForm({ ...form, empfaengerName: e.target.value })} /></Field>
            <Field label="Zusatz"><Input value={form.empfaengerZusatz} onChange={e => setForm({ ...form, empfaengerZusatz: e.target.value })} /></Field>
            <Field label="Straße"><Input value={form.empfaengerStrasse} onChange={e => setForm({ ...form, empfaengerStrasse: e.target.value })} /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="PLZ"><Input value={form.empfaengerPlz} onChange={e => setForm({ ...form, empfaengerPlz: e.target.value })} /></Field>
              <div className="col-span-2">
                <Field label="Ort"><Input value={form.empfaengerOrt} onChange={e => setForm({ ...form, empfaengerOrt: e.target.value })} /></Field>
              </div>
            </div>
            <Field label="Notizen"><Textarea rows={2} value={form.notizen} onChange={e => setForm({ ...form, notizen: e.target.value })} /></Field>
            <FormActions>
              <Button type="button" variant="secondary" onClick={() => setEditEmpfaenger(false)}>Abbrechen</Button>
              <Button type="button" loading={busy} onClick={speichereEmpfaenger}>Speichern</Button>
            </FormActions>
          </div>
        ) : (
          <>
            <Row label="Kunde" value={
              <Link to={`/kunden/${r.kundeId}`} className="text-brand-700 hover:underline">
                {r.kundeName} {r.kundennummer ? `(${r.kundennummer})` : ''}
              </Link>
            } />
            <Row label="Empfänger" value={
              <span className="whitespace-pre-line text-right">
                {[r.empfaengerName, r.empfaengerZusatz, r.empfaengerStrasse,
                  [r.empfaengerPlz, r.empfaengerOrt].filter(Boolean).join(' ')]
                  .filter(Boolean).join('\n')}
              </span>
            } />
            <Row label="Rechnungsnummer" value={r.rechnungsnummer && <span className={`rounded px-1 ${nummerNeu.className}`}>{r.rechnungsnummer}</span>} />
            <Row label="Rechnungsdatum" value={fmtDatum(r.rechnungsdatum)} />
            <Row label="Fällig am" value={
              <span className={r.ueberfaellig ? 'text-red-700' : undefined}>{fmtDatum(r.faelligAm)}</span>
            } />
            <Row label="Abrechnungsjahr" value={r.abrechnungsjahr} />
            <Row label="Notizen" value={r.notizen} />
          </>
        )}
      </Card>

      {/* Positionen */}
      <Card className="mb-6 p-6">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-gray-900">Positionen</h2>
          {entwurf && (
            <Button size="sm" variant="secondary" onClick={() => setZeigePositionModal(true)}
              icon={<Icon.Plus className="h-4 w-4" />}>
              Position hinzufügen
            </Button>
          )}
        </div>

        {zeigePositionModal && (
          <PositionModal
            rechnungId={r.id}
            onClose={() => setZeigePositionModal(false)}
            onSaved={neu => {
              // Die API liefert die ganze Rechnung – neu ist die Position, die vorher fehlte.
              const bisher = new Set(r.positionen.map(p => p.id));
              neu.positionen.filter(p => !bisher.has(p.id)).forEach(p => markiereNeu(`rechnungsposition-${p.id}`));
              setZeigePositionModal(false); load();
            }}
          />
        )}

        {entwurf && r.positionen.length === 0 && (
          <p className="py-3 text-sm text-gray-500">
            Noch keine Positionen – ohne Position lässt sich die Rechnung nicht stellen.{' '}
            <button onClick={() => setZeigePositionModal(true)} className="font-medium text-brand-700 hover:underline">
              Position hinzufügen
            </button>
          </p>
        )}

        {/* Gestapelte Zeilen statt breiter Tabelle – iPad hochkant (~504 px). */}
        <ul className="divide-y divide-gray-100">
          {r.positionen.map(p => { const a = ankunft(`rechnungsposition-${p.id}`); return (
            <li key={p.id} ref={a.ref} className={`py-3 ${a.className}`}>
              <div className="flex items-baseline gap-2">
                <span className="w-5 shrink-0 text-xs text-gray-400">{p.positionNr}.</span>
                <span className="min-w-0 flex-1 break-words text-sm font-medium text-gray-900">
                  {p.bezeichnung}
                </span>
                <span className="whitespace-nowrap text-sm font-medium tabular-nums text-gray-900">
                  {formatBetrag(p.brutto)}
                </span>
              </div>
              <div className="ml-7 mt-0.5 flex items-baseline gap-2 text-xs text-gray-500">
                <span className="min-w-0 flex-1 tabular-nums">
                  {p.menge} × {formatPreis(p.einzelpreis)} · {formatMwst(p.mwstSatz)}
                  {p.leistungVon && (
                    <span className="block">
                      {p.leistungBis && p.leistungBis !== p.leistungVon
                        ? `Leistungszeitraum ${fmtDatum(p.leistungVon)} – ${fmtDatum(p.leistungBis)}`
                        : `Leistung am ${fmtDatum(p.leistungVon)}`}
                      {p.vertragsnummer && ` · ${p.vertragsnummer}`}
                    </span>
                  )}
                </span>
                {entwurf && (
                  <button
                    onClick={() => aktion({ erfolg: 'Position entfernt', fehler: 'Position konnte nicht entfernt werden' },
                      () => rechnungService.deletePosition(r.id, p.id), `Position „${p.bezeichnung}“ entfernen?`)}
                    className="shrink-0 text-red-600 hover:underline"
                  >
                    Entfernen
                  </button>
                )}
              </div>
            </li>
          ); })}
        </ul>

        <dl className="mt-4 space-y-1 border-t border-gray-200 pt-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Netto</dt>
            <dd className="tabular-nums text-gray-900">{formatBetrag(r.summeNetto)}</dd>
          </div>
          {r.mwstJeSatz.map(a => (
            <div key={a.satz} className="flex justify-between gap-4">
              <dt className="text-gray-500">zzgl. {formatMwst(a.satz)} USt auf {formatBetrag(a.netto)}</dt>
              <dd className="tabular-nums text-gray-900">{formatBetrag(a.mwst)}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-4 border-t border-gray-100 pt-1">
            <dt className="font-semibold text-gray-900">Gesamtbetrag</dt>
            <dd className="tabular-nums font-semibold text-gray-900">{formatBetrag(r.summeBrutto)}</dd>
          </div>
        </dl>
      </Card>

    </div>
  );
};

export default RechnungDetail;
